// src/components/OfferList/OfferList.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaDownload, FaTrash, FaFileExcel, FaCheckSquare, FaRegSquare, FaRegStickyNote } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import Navbar from '../Navbar/Navbar';
import api from '../Axios/axios';
import './OfferList.scss';

const t = {
  all: 'T\u00fcm\u00fc',
  none: 'Hi\u00e7biri',
  pending: 'Beklemede',
  rented: 'Kiraland\u0131',
  rentedElsewhere: 'Ba\u015fka Yerde Kiraland\u0131',
  projectCancelled: 'Proje \u0130ptal Edildi',
  sold: 'Sat\u0131ld\u0131',
  takenFromElsewhere: 'Ba\u015fkas\u0131 Taraf\u0131ndan Sat\u0131ld\u0131',
  rentalOffers: 'Kiral\u0131k Teklifler',
  salesOffers: 'Sat\u0131l\u0131k Teklifler',
  rental: 'Kiral\u0131k',
  sales: 'Sat\u0131l\u0131k',
  pdfDownload: 'PDF \u0130ND\u0130R',
  tenantCompany: 'Kirac\u0131 Firma',
  buyerCompany: 'Al\u0131c\u0131 Firma',
  machineModel: 'Makine Modeli',
  machine: 'Makine',
  senderCompany: 'G\u00f6nderen Firma',
  createdDate: 'Olu\u015fturma Tarihi',
  workSite: '\u015eantiye',
  rentalPeriod: 'S\u00fcre (Ay)',
  rentAmount: 'Kira Bedeli',
  startDate: 'Ba\u015flang\u0131\u00e7',
  endDate: 'Biti\u015f',
  status: 'Durum',
  actions: '\u0130\u015flemler',
  notes: 'Notlar',
  note: 'Not',
  noNote: 'Ekle',
  hasNote: 'Not',
  saveNote: 'Notu Kaydet',
  close: 'Kapat',
  noteSaved: 'Not kaydedildi.',
  noteSaveFailed: 'Not kaydedilemedi.',
  piece: 'Adet',
  price: 'Fiyat',
  today: 'Bug\u00fcn',
  thisWeek: 'Bu Hafta',
  thisMonth: 'Bu Ay',
  last3Months: 'Son 3 Ay',
  allDates: 'Hepsi',
  selectOffer: 'Teklifi se\u00e7',
  selectPdfOffers: "PDF'i olan teklifleri se\u00e7",
  selectedPdfTitle: "Se\u00e7ilen teklif PDF'lerini indir",
  pdfDownloadTitle: 'PDF \u0130ndir',
  edit: 'D\u00fczenle',
  deleteConfirm: 'Bu teklifi silmek istedi\u011finize emin misiniz?',
  deleteSuccess: 'Teklif silindi.',
  deleteFailed: 'Silme ba\u015far\u0131s\u0131z: ',
  unknownError: 'Bilinmeyen hata',
  statusUpdateFailed: 'Durum g\u00fcncellenemedi.',
  selectPdfWarning: 'PDF dosyas\u0131 olan en az bir teklif se\u00e7in.',
  exportEmpty: 'D\u0131\u015fa aktar\u0131lacak veri bulunamad\u0131.',
  rowNo: 'S\u0131ra No',
};

const RENTAL_STATUS_OPTIONS = [
  { value: '', label: t.all },
  { value: 'None', label: t.none },
  { value: 'Pending', label: t.pending },
  { value: 'Rented', label: t.rented },
  { value: 'RentedElsewhere', label: t.rentedElsewhere },
  { value: 'ProjectCancelled', label: t.projectCancelled },
];

const SALES_STATUS_OPTIONS = [
  { value: '', label: t.all },
  { value: 'None', label: t.none },
  { value: 'Sold', label: t.sold },
  { value: 'TakenFromElsewhere', label: t.takenFromElsewhere },
  { value: 'ProjectCancelled', label: t.projectCancelled },
  { value: 'Pending', label: t.pending },
];

export default function OfferList() {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState('');
  const [mode, setMode] = useState('rental');
  const [offers, setOffers] = useState([]);
  const [filters, setFilters] = useState({});
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedOfferIds, setSelectedOfferIds] = useState([]);
  const [noteModal, setNoteModal] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteMessage, setNoteMessage] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(window.atob(base64));
      setUserRole(decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']);
    } catch (error) {
      console.error('Token decode error:', error);
    }
  }, []);

  const normalize = (str) => String(str || '').toLocaleLowerCase('tr-TR').trim();

  const titleCase = (s) => {
    const lower = normalize(s);
    return lower ? lower.charAt(0).toLocaleUpperCase('tr-TR') + lower.slice(1) : '';
  };

  const getShortCompany = (name) => {
    const s = String(name || '')
      .replace(/İ/g, 'I')
      .replace(/ı/g, 'i')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('en-US')
      .trim();
    if (s.includes('turkish tower cranes') || s.includes('ttc')) return 'TTC';
    if (s.includes('star kule vinc')) return 'Star Kule Vin\u00e7';
    return name || '-';
  };

  const formatDate = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : '';

  const formatMoney = (v, currency) => {
    const num = Number(v);
    if (!isFinite(num)) return '';
    return `${num.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || ''}`.trim();
  };

  const columns = useMemo(() => {
    if (mode === 'rental') {
      return [
        { key: 'index', label: '#' },
        { key: 'tenantCompanyName', label: t.tenantCompany },
        { key: 'machine', label: t.machineModel },
        { key: 'rentingCompany', label: t.senderCompany },
        { key: 'createdDate', label: t.createdDate },
        { key: 'buildingSite', label: t.workSite },
        { key: 'rentalPeriod', label: t.rentalPeriod },
        { key: 'rent', label: t.rentAmount },
        { key: 'startDate', label: t.startDate },
        { key: 'endDate', label: t.endDate },
        { key: 'status', label: t.status },
        { key: 'note', label: t.notes },
        { key: 'actions', label: t.actions },
      ];
    }

    return [
      { key: 'index', label: '#' },
      { key: 'buyerCompanyName', label: t.buyerCompany },
      { key: 'machine', label: t.machine },
      { key: 'rentingCompany', label: t.senderCompany },
      { key: 'createdDate', label: t.createdDate },
      { key: 'piece', label: t.piece },
      { key: 'price', label: t.price },
      { key: 'status', label: t.status },
      { key: 'note', label: t.notes },
      { key: 'actions', label: t.actions },
    ];
  }, [mode]);

  const filterableKeys = useMemo(() => {
    return mode === 'rental'
      ? ['tenantCompanyName', 'machine', 'rentingCompany', 'buildingSite', 'status']
      : ['buyerCompanyName', 'machine', 'rentingCompany', 'status'];
  }, [mode]);

  const statusOptions = mode === 'rental' ? RENTAL_STATUS_OPTIONS : SALES_STATUS_OPTIONS;
  const apiRoot = (api.defaults.baseURL || '').replace(/\/api\/?$/, '');

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const url = mode === 'rental' ? '/RentalOffer' : '/SalesOffer';
        const res = await api.get(url);
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
            ? res.data.data
            : [];
        setOffers(list);
      } catch (error) {
        console.error('Teklifler cekilirken hata:', error);
        setOffers([]);
      }
    };

    fetchOffers();
    setFilters({});
    setDateFilter('all');
    setSelectedOfferIds([]);
  }, [mode]);

  const handleStatusChange = async (offerId, newStatus) => {
    try {
      const url = mode === 'rental' ? '/RentalOffer/Status' : '/SalesOffer/Status';
      await api.put(url, { id: offerId, status: newStatus });
      setOffers((prev) => prev.map((o) => (o.id === offerId ? { ...o, status: newStatus } : o)));
    } catch (error) {
      console.error('Status guncellenirken hata:', error);
      alert(t.statusUpdateFailed);
    }
  };

  const openNoteModal = (offer) => {
    setNoteModal(offer);
    setNoteDraft(offer.note || '');
    setNoteMessage('');
  };

  const closeNoteModal = () => {
    if (isSavingNote) return;
    setNoteModal(null);
    setNoteDraft('');
    setNoteMessage('');
  };

  const handleSaveNote = async () => {
    if (!noteModal) return;

    setIsSavingNote(true);
    setNoteMessage('');

    try {
      const url = mode === 'rental' ? '/RentalOffer/Note' : '/SalesOffer/Note';
      await api.put(url, { id: noteModal.id, note: noteDraft });
      setOffers((prev) => prev.map((o) => (o.id === noteModal.id ? { ...o, note: noteDraft } : o)));
      setNoteMessage(t.noteSaved);
      setTimeout(() => {
        setNoteModal(null);
        setNoteDraft('');
        setNoteMessage('');
      }, 450);
    } catch (error) {
      console.error('Not kaydedilirken hata:', error);
      setNoteMessage(error.response?.data?.message || t.noteSaveFailed);
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleDelete = async (offerId) => {
    if (!window.confirm(t.deleteConfirm)) return;

    try {
      const url = mode === 'rental' ? '/RentalOffer' : '/SalesOffer';
      await api.delete(url, {
        params: { id: Number(offerId) },
      });

      setOffers((prev) => prev.filter((o) => o.id !== offerId));
      alert(t.deleteSuccess);
    } catch (error) {
      console.error('Silme hatasi:', error);
      const msg = error.response?.data?.message || error.message || t.unknownError;
      alert(t.deleteFailed + msg);
    }
  };

  const handleEditClick = (id) => {
    if (mode === 'rental') {
      navigate(`/offeredit/${id}`);
    } else {
      navigate(`/sellofferedit/${id}`);
    }
  };

  const uniqueValues = useMemo(() => {
    return filterableKeys.reduce((acc, key) => {
      const vals = offers
        .map((o) => {
          if (key === 'status') return normalize(o.status || '');
          const raw = o[key] != null ? String(o[key]) : '';
          if (key === 'rentingCompany') return normalize(getShortCompany(raw));
          return normalize(raw);
        })
        .filter((v) => v !== '')
        .filter((v, i, arr) => arr.indexOf(v) === i)
        .sort((a, b) => a.localeCompare(b, 'tr-TR'));
      acc[key] = vals;
      return acc;
    }, {});
  }, [offers, filterableKeys]);

  const filteredOffers = useMemo(() => {
    const now = new Date();
    let weekAgo;
    let threeMonthsAgo;

    if (dateFilter === 'week') {
      weekAgo = new Date(now);
      weekAgo.setDate(now.getDate() - 7);
    }
    if (dateFilter === '3months') {
      threeMonthsAgo = new Date(now);
      threeMonthsAgo.setMonth(now.getMonth() - 3);
    }

    return offers.filter((o) => {
      const passText = filterableKeys.every((key) => {
        const f = filters[key];
        if (!f) return true;
        if (key === 'status') return o.status === f;
        const raw = key === 'rentingCompany' ? getShortCompany(o[key]) : o[key] != null ? String(o[key]) : '';
        return normalize(raw) === f;
      });
      if (!passText) return false;

      if (dateFilter !== 'all') {
        const created = o.createdDate ? new Date(o.createdDate) : null;
        if (!created) return false;

        switch (dateFilter) {
          case 'today':
            if (created.toDateString() !== now.toDateString()) return false;
            break;
          case 'week':
            if (created < weekAgo) return false;
            break;
          case 'month':
            if (created.getMonth() !== now.getMonth() || created.getFullYear() !== now.getFullYear()) return false;
            break;
          case '3months':
            if (created < threeMonthsAgo) return false;
            break;
          default:
            break;
        }
      }

      return true;
    });
  }, [offers, filters, dateFilter, filterableKeys]);

  const sortedOffers = useMemo(() => {
    return [...filteredOffers].sort((a, b) => {
      const da = a.createdDate ? new Date(a.createdDate).getTime() : 0;
      const db = b.createdDate ? new Date(b.createdDate).getTime() : 0;
      return db - da;
    });
  }, [filteredOffers]);

  const downloadableOffers = useMemo(
    () => sortedOffers.filter((offer) => Boolean(offer.pdfFile)),
    [sortedOffers]
  );

  const selectedDownloadableOffers = useMemo(
    () => downloadableOffers.filter((offer) => selectedOfferIds.includes(offer.id)),
    [downloadableOffers, selectedOfferIds]
  );

  useEffect(() => {
    setSelectedOfferIds((prev) =>
      prev.filter((id) => sortedOffers.some((offer) => offer.id === id && offer.pdfFile))
    );
  }, [sortedOffers]);

  const isAllDownloadableSelected =
    downloadableOffers.length > 0 && selectedDownloadableOffers.length === downloadableOffers.length;

  const toggleSelectOffer = (offerId) => {
    setSelectedOfferIds((prev) =>
      prev.includes(offerId) ? prev.filter((id) => id !== offerId) : [...prev, offerId]
    );
  };

  const toggleSelectAllDownloadable = () => {
    setSelectedOfferIds(isAllDownloadableSelected ? [] : downloadableOffers.map((offer) => offer.id));
  };

  const handleDownloadSelectedPdfs = () => {
    if (selectedDownloadableOffers.length === 0) {
      alert(t.selectPdfWarning);
      return;
    }

    selectedDownloadableOffers.forEach((offer, index) => {
      window.setTimeout(() => {
        const link = document.createElement('a');
        link.href = `${apiRoot}/pdfs/${encodeURIComponent(offer.pdfFile)}`;
        link.download = offer.pdfFile;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, index * 250);
    });
  };

  const handleExportExcel = () => {
    if (sortedOffers.length === 0) {
      alert(t.exportEmpty);
      return;
    }

    const dataToExport = sortedOffers.map((o, index) => {
      const statusLabel = statusOptions.find((s) => s.value === o.status)?.label || o.status;
      const base = {
        [t.rowNo]: index + 1,
        [t.machineModel]: o.machine || '-',
        [t.senderCompany]: getShortCompany(o.rentingCompany),
        [t.createdDate]: formatDate(o.createdDate),
        [t.status]: statusLabel,
      };

      if (mode === 'rental') {
        return {
          ...base,
          [t.tenantCompany]: o.tenantCompanyName || '-',
          [t.workSite]: titleCase(o.buildingSite || ''),
          [t.rentalPeriod]: o.rentalPeriod || '-',
          [t.rentAmount]: o.rent != null ? `${Number(o.rent).toLocaleString('tr-TR')} TL` : '-',
          [t.startDate]: formatDate(o.startDate),
          [t.endDate]: formatDate(o.endDate),
        };
      }

      return {
        ...base,
        [t.buyerCompany]: o.buyerCompanyName || '-',
        [t.piece]: o.piece || '-',
        [t.price]: formatMoney(o.price, o.currency),
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const colWidths = Object.keys(dataToExport[0]).map((key) => ({ wch: Math.max(key.length, 20) }));
    worksheet['!cols'] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, mode === 'rental' ? t.rentalOffers : t.salesOffers);

    const fileName = `${mode === 'rental' ? 'Kiralik_Teklifler' : 'Satilik_Teklifler'}_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const getRowStatusClass = (status) => {
    if (mode === 'rental') {
      switch (status) {
        case 'Pending': return 'status-pending';
        case 'Rented': return 'status-success';
        case 'RentedElsewhere': return 'status-elsewhere';
        case 'ProjectCancelled': return 'status-cancelled';
        default: return 'status-none';
      }
    }

    switch (status) {
      case 'Pending': return 'status-pending';
      case 'Sold': return 'status-success';
      case 'TakenFromElsewhere': return 'status-elsewhere';
      case 'ProjectCancelled': return 'status-cancelled';
      default: return 'status-none';
    }
  };

  return (
    <div className="offer-list">
      <Navbar />

      <div className="page-head">
        <div className="title-group">
          <h2>{mode === 'rental' ? t.rentalOffers : t.salesOffers}</h2>
          <button
            className="bulk-download-btn"
            onClick={handleDownloadSelectedPdfs}
            disabled={selectedDownloadableOffers.length === 0}
            title={t.selectedPdfTitle}
          >
            <FaDownload style={{ marginRight: '5px' }} />
            {t.pdfDownload} ({selectedDownloadableOffers.length})
          </button>
        </div>

        <div className="actions-group">
          <button className="excel-btn" onClick={handleExportExcel} title="Excel'e Aktar">
            <FaFileExcel style={{ marginRight: '5px' }} /> Excel
          </button>

          <div className="mode-toggle">
            <button
              className={`toggle-btn ${mode === 'rental' ? 'active' : ''}`}
              onClick={() => setMode('rental')}
            >
              {t.rental}
            </button>
            <button
              className={`toggle-btn ${mode === 'sales' ? 'active' : ''}`}
              onClick={() => setMode('sales')}
            >
              {t.sales}
            </button>
          </div>
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th className="select-col">
                <button
                  type="button"
                  className="select-all-btn"
                  onClick={toggleSelectAllDownloadable}
                  disabled={downloadableOffers.length === 0}
                  title={t.selectPdfOffers}
                >
                  {isAllDownloadableSelected ? <FaCheckSquare /> : <FaRegSquare />}
                </button>
              </th>
              {columns.map((col) => (
                <th key={col.key} className={`col-${col.key} ${filters[col.key] ? 'filter-active' : ''}`}>
                  <div className="th-inner">
                    <span>{col.label}</span>
                    {col.key === 'createdDate' ? (
                      <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                        <option value="all">{t.allDates}</option>
                        <option value="today">{t.today}</option>
                        <option value="week">{t.thisWeek}</option>
                        <option value="month">{t.thisMonth}</option>
                        <option value="3months">{t.last3Months}</option>
                      </select>
                    ) : filterableKeys.includes(col.key) ? (
                      <select value={filters[col.key] || ''} onChange={(e) => setFilters({ ...filters, [col.key]: e.target.value })}>
                        <option value="">{t.all}</option>
                        {(col.key === 'status' ? statusOptions : uniqueValues[col.key] || []).map((opt) => {
                          const val = col.key === 'status' ? opt.value : opt;
                          const lbl = col.key === 'status' ? opt.label : titleCase(opt);
                          return <option key={val} value={val}>{lbl}</option>;
                        })}
                      </select>
                    ) : null}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {sortedOffers.map((o, i) => (
              <tr key={o.id} className={getRowStatusClass(o.status)}>
                <td className="select-col">
                  <input
                    type="checkbox"
                    checked={selectedOfferIds.includes(o.id)}
                    disabled={!o.pdfFile}
                    onChange={() => toggleSelectOffer(o.id)}
                    aria-label={t.selectOffer}
                  />
                </td>
                <td className="col-index">{i + 1}</td>
                {mode === 'rental' ? (
                  <>
                    <td className="col-tenantCompanyName">{o.tenantCompanyName || '-'}</td>
                    <td className="col-machine">{o.machine || '-'}</td>
                    <td className="col-rentingCompany">{getShortCompany(o.rentingCompany)}</td>
                    <td className="col-createdDate">{formatDate(o.createdDate)}</td>
                    <td className="col-buildingSite">{titleCase(o.buildingSite || '')}</td>
                    <td className="col-rentalPeriod">{o.rentalPeriod ?? '-'}</td>
                    <td className="col-rent">{o.rent != null ? `${Number(o.rent).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL` : '-'}</td>
                    <td className="col-startDate">{formatDate(o.startDate)}</td>
                    <td className="col-endDate">{formatDate(o.endDate)}</td>
                  </>
                ) : (
                  <>
                    <td className="col-buyerCompanyName">{o.buyerCompanyName || '-'}</td>
                    <td className="col-machine">{o.machine || '-'}</td>
                    <td className="col-rentingCompany">{getShortCompany(o.rentingCompany)}</td>
                    <td className="col-createdDate">{formatDate(o.createdDate)}</td>
                    <td className="col-piece">{o.piece ?? '-'}</td>
                    <td className="col-price">{formatMoney(o.price, o.currency)}</td>
                  </>
                )}
                <td className="col-status">
                  <select value={o.status || ''} onChange={(e) => handleStatusChange(o.id, e.target.value)}>
                    {statusOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </td>

                <td className="note-cell col-note">
                  <button
                    type="button"
                    className={`note-button ${o.note ? 'has-note' : ''}`}
                    onClick={() => openNoteModal(o)}
                    title={t.notes}
                  >
                    <FaRegStickyNote size={12} />
                    {o.note ? t.hasNote : t.noNote}
                  </button>
                </td>

                <td className="actions-cell col-actions">
                  <div className="actions-inline">
                    {o.pdfFile && (
                      <a href={`${apiRoot}/pdfs/${encodeURIComponent(o.pdfFile)}`} download className="icon-button" title={t.pdfDownloadTitle}>
                        <FaDownload size={16} />
                      </a>
                    )}

                    <button className="icon-button" onClick={() => handleEditClick(o.id)} title={t.edit}>
                      <FaEdit size={16} />
                    </button>

                    {userRole === 'Owner' && (
                      <button className="icon-button delete-btn" onClick={() => handleDelete(o.id)} title="Sil">
                        <FaTrash size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {noteModal && (
        <div className="note-modal-backdrop" onMouseDown={closeNoteModal}>
          <div className="note-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="note-modal-head">
              <div>
                <h3>{t.notes}</h3>
                <p>{mode === 'rental' ? noteModal.tenantCompanyName : noteModal.buyerCompanyName}</p>
              </div>
              <button type="button" onClick={closeNoteModal} aria-label={t.close}>×</button>
            </div>

            <textarea
              value={noteDraft}
              onChange={(event) => setNoteDraft(event.target.value)}
              placeholder="Bu teklif için iç takip notu yazın."
              rows={7}
            />

            {noteMessage && <div className="note-message">{noteMessage}</div>}

            <div className="note-modal-actions">
              <button type="button" className="secondary" onClick={closeNoteModal} disabled={isSavingNote}>
                {t.close}
              </button>
              <button type="button" className="primary" onClick={handleSaveNote} disabled={isSavingNote}>
                {isSavingNote ? 'Kaydediliyor...' : t.saveNote}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
